import React, { useCallback, useEffect, useState } from 'react'
import coreLogger from '@adc/logger'
import analytics from '@adc/analytics'
import { user } from '@adc/blueprint'
import type * as blueprint from '@adc/blueprint'
import { enums } from '@adc/sdk'
import {
  Text,
  Icon,
  Modal,
  Button,
  CheckboxField,
  PhoneVerification,
  Alert,
  constants,
} from '@adc/ui-core'
import classnames from 'classnames/bind'
import styles from './styles.scss'

const cx = classnames.bind(styles)
const { ColorNames } = constants
const logger = coreLogger.forModule('notifyMeModal')
const { track, eventNames } = analytics

const CONSENT_MESSAGE =
  'You are signing your written consent to receive communications from Auction.com and its products like SmartSale, via text and phone at the number you provided and email, including by using an automated dialer or prerecorded or artificial message, for marketing and commercial purposes. Consent is not a condition of purchase. Message frequency varies. You may opt-out at any time. Message and data rates may apply.'

type ModalStep = 'initial' | 'phoneCapture'

interface LatestPhone {
  number?: string
  opt_in?: boolean
  verified?: boolean
}

interface NotifyMeModalProps {
  open: boolean
  onClose: () => void
  onConfirm: (phone?: string) => void
  partyId: string
  latestPhone?: LatestPhone
  onVerificationSuccess?: (newPhone: { number: string }) => void
  savedPropertiesUrl?: string
  /**
   * Communication-preferences controller (blueprint `useCommunicationPreferences`).
   * Owns the happy-path state and the SMS/email marketing opt-in writes.
   */
  communicationPreferences?: blueprint.communicationPreferences.CommunicationPreferencesController
  /**
   * Formatted phone number the user must text START to in order to re-enable
   * SMS after a Twilio opt-out. Supplied by the app (e.g. from @adc/constants)
   * so the shortcode/number can change without a design-system release.
   */
  smsOptOutNumber?: string
}

/**
 * Notify Me 2.0 Modal — Credit Bid notification enrollment.
 *
 * 2-step progressive flow:
 * 1. Initial: Alert Methods overview (email enabled, text not enabled) OR happy path (both enabled)
 * 2. Phone Capture: phone input + verification code + consent checkbox
 *
 * After successful phone capture, returns to Initial step in "happy path" state.
 */
const NotifyMeModal = ({
  open,
  onClose,
  onConfirm,
  partyId,
  latestPhone = {},
  onVerificationSuccess,
  savedPropertiesUrl = '/dashboard/saved-properties/',
  communicationPreferences,
  smsOptOutNumber,
}: NotifyMeModalProps) => {
  // SMS marketing preference drives the happy-path.
  const isSmsEnrolled = Boolean(communicationPreferences?.isSmsMarketingOptedIn)
  // Twilio opt-out (external STOP) can't be reversed via API — user must text
  // START to re-enable. Show the guidance box on the opt-in step and disable
  // submit; the rest of the flow is unchanged.
  const isTwilioBlocked = Boolean(
    communicationPreferences?.isSmsMarketingTwilioBlocked
  )

  const [step, setStep] = useState<ModalStep>('initial')
  const [phoneNumber, setPhoneNumber] = useState(latestPhone?.number || '')
  const [isPhoneVerified, setIsPhoneVerified] = useState(
    Boolean(latestPhone?.verified)
  )
  const [consentChecked, setConsentChecked] = useState(true)
  const [isTextAlreadyEnabled, setIsTextAlreadyEnabled] =
    useState(isSmsEnrolled)

  // Initialize state only when the modal transitions to open. Depending on
  // latestPhone would reset the flow mid-way, because the parent re-derives a
  // new latestPhone object reference on every render.
  useEffect(() => {
    if (open) {
      setIsTextAlreadyEnabled(isSmsEnrolled)
      setStep('initial')
      setPhoneNumber(latestPhone?.number || '')
      setIsPhoneVerified(Boolean(latestPhone?.verified))
      setConsentChecked(true)
      track(eventNames.notifyMeModalShown, {
        isEmailOptedIn: Boolean(
          communicationPreferences?.isEmailMarketingOptedIn
        ),
        isSmsOptedIn: Boolean(communicationPreferences?.isSmsMarketingOptedIn),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleTurnOnTextAlerts = useCallback(() => {
    track(eventNames.notifyMeTurnOnTextClick)
    setStep('phoneCapture')
  }, [])

  const handleConfirmTextAlerts = useCallback(async () => {
    track(eventNames.notifyMeConfirmAlertsClick)
    try {
      await user.providers.updateOptInInformation({
        phone: phoneNumber,
        isOptIn: true,
      })
      // MERCH-6: enroll in SMS marketing. Confirm = full enrollment.
      communicationPreferences
        ?.optIn(enums.CommunicationPreferenceChannel.Sms)
        .then(() => track(eventNames.notifyMeEnrollmentComplete))
        .catch(logger.error)
      onVerificationSuccess?.({ number: phoneNumber })
      setIsTextAlreadyEnabled(true)
      setStep('initial')
    } catch {
      // PhoneVerification handles its own error display
    }
  }, [phoneNumber, onVerificationSuccess, communicationPreferences])

  // MERCH-7: enroll in email marketing on close (no-op if already opted in).
  const handleClose = useCallback(() => {
    if (
      communicationPreferences &&
      !communicationPreferences.isEmailMarketingOptedIn
    ) {
      communicationPreferences
        .optIn(enums.CommunicationPreferenceChannel.Email)
        .then(() => track(eventNames.notifyMeEmailEnrolled))
        .catch(logger.error)
    }
    // Closed via Done for Now or X without enabling text = abandon.
    if (!isTextAlreadyEnabled) {
      track(eventNames.notifyMeModalDismissed, {
        modal_state: step === 'phoneCapture' ? 'phone_capture' : 'initial',
      })
    }
    onClose()
  }, [communicationPreferences, onClose, isTextAlreadyEnabled, step])

  const handleDoneForNow = useCallback(() => {
    track(eventNames.notifyMeDoneForNowClick)
    onConfirm()
    handleClose()
  }, [onConfirm, handleClose])

  if (!open) return null

  return (
    <Modal
      open
      size='tiny'
      fullScreen='never'
      elmId='notify_me_modal'
      onClose={handleClose}
      closeIcon={
        <Icon
          className='close'
          name='xmark'
          iconStyle='regular'
          size='big'
          color={ColorNames.Gray}
          data-elm-id='notify_me_modal_close'
        />
      }
      slideUp='mobileOnly'
    >
      <Modal.Content>
        {step === 'initial' && (
          <InitialStep
            isTextAlreadyEnabled={isTextAlreadyEnabled}
            onTurnOnTextAlerts={handleTurnOnTextAlerts}
            onDoneForNow={handleDoneForNow}
            onDone={handleDoneForNow}
            savedPropertiesUrl={savedPropertiesUrl}
          />
        )}
        {step === 'phoneCapture' && (
          <PhoneCaptureStep
            partyId={partyId}
            latestPhone={
              // After verification, the core form calls reset() back to
              // initialValues.phone. Feed the just-verified number as the
              // effective latestPhone so enableReinitialize restores it
              // instead of clearing the field.
              isPhoneVerified && phoneNumber
                ? { ...latestPhone, number: phoneNumber, verified: true }
                : latestPhone
            }
            onPhoneVerified={(newPhone: { number: string }) => {
              // Code verified — only enable the Confirm button. Do NOT fire
              // onVerificationSuccess or any opt-in side effect here; that
              // happens when the user clicks "Confirm Text Alerts".
              track(eventNames.notifyMePhoneVerified)
              setPhoneNumber(newPhone.number)
              setIsPhoneVerified(true)
            }}
            consentChecked={consentChecked}
            onConsentChange={setConsentChecked}
            isPhoneVerified={isPhoneVerified}
            onConfirm={handleConfirmTextAlerts}
            isTwilioBlocked={isTwilioBlocked}
            smsOptOutNumber={smsOptOutNumber}
          />
        )}
      </Modal.Content>
    </Modal>
  )
}

// --- Sub-components ---

interface InitialStepProps {
  isTextAlreadyEnabled: boolean
  onTurnOnTextAlerts: () => void
  onDoneForNow: () => void
  onDone: () => void
  savedPropertiesUrl: string
}

const InitialStep = ({
  isTextAlreadyEnabled,
  onTurnOnTextAlerts,
  onDoneForNow,
  onDone,
  savedPropertiesUrl,
}: InitialStepProps) => (
  <div className={cx('step-container')}>
    <div className={cx('row-header')}>
      <Text
        as='h2'
        size='huge'
        weight='heavy'
        color={ColorNames.GrayDark}
        data-elm-id='notify_me_modal_title'
      >
        {isTextAlreadyEnabled ? "You're all set!" : "You'll be notified"}
      </Text>
      <Text
        as='p'
        size='small'
        color={ColorNames.Gray}
        data-elm-id='notify_me_modal_subtitle'
      >
        {isTextAlreadyEnabled
          ? "We'll send you a real-time alert when the Credit Bid is available."
          : 'Emails are good, but texts are faster. Turn on Text Alerts now.'}
      </Text>
    </div>

    <div
      className={cx('row-methods', {
        'row-methods-happy-path': isTextAlreadyEnabled,
        'row-methods-text-not-enabled': !isTextAlreadyEnabled,
      })}
    >
      <Text
        as='p'
        size='small'
        weight='medium'
        color={ColorNames.Gray}
        style={{ marginBottom: 0 }}
      >
        Alert Methods
      </Text>

      <AlertMethodCard
        iconName='envelope'
        title='Email Alerts'
        description="We'll email you when there are updates to your saved properties."
        enabled
        elmId='notify_me_modal_email'
      />

      <AlertMethodCard
        iconName='mobile'
        title='Text Alerts'
        description='Get instant alerts for updates to your saved properties. Turn off anytime.'
        enabled={isTextAlreadyEnabled}
        showFasterBadge={!isTextAlreadyEnabled}
        highlighted={!isTextAlreadyEnabled}
        elmId='notify_me_modal_text'
      />

      {isTextAlreadyEnabled && (
        <a
          href={savedPropertiesUrl}
          className={cx('saved-link')}
          data-elm-id='notify_me_modal_view_saved'
        >
          View saved properties
        </a>
      )}
    </div>

    <div
      className={cx('button-group', {
        'button-group-happy-path': isTextAlreadyEnabled,
        'button-group-reverse-mobile': !isTextAlreadyEnabled,
      })}
    >
      {isTextAlreadyEnabled ? (
        <Button
          role='primary'
          isStretch
          elmId='notify_me_modal_done'
          onClick={onDone}
        >
          Done
        </Button>
      ) : (
        <>
          <Button
            role='secondary'
            color={ColorNames.Gray}
            isStretch
            elmId='notify_me_modal_done_for_now'
            onClick={onDoneForNow}
          >
            Done for Now
          </Button>
          <Button
            role='primary'
            isStretch
            elmId='notify_me_modal_turn_on_text'
            onClick={onTurnOnTextAlerts}
          >
            Turn On Text Alerts
          </Button>
        </>
      )}
    </div>
  </div>
)

interface PhoneCaptureStepProps {
  partyId: string
  latestPhone: LatestPhone
  onPhoneVerified: (newPhone: { number: string }) => void
  consentChecked: boolean
  onConsentChange: (checked: boolean) => void
  isPhoneVerified: boolean
  onConfirm: () => void
  isTwilioBlocked?: boolean
  smsOptOutNumber?: string
}

const PhoneCaptureStep = ({
  partyId,
  latestPhone,
  onPhoneVerified,
  consentChecked,
  onConsentChange,
  isPhoneVerified,
  onConfirm,
  isTwilioBlocked = false,
  smsOptOutNumber,
}: PhoneCaptureStepProps) => (
  <div className={cx('step-container')}>
    <div className={cx('step-header')}>
      <Text
        as='h2'
        size='huge'
        weight='heavy'
        color={ColorNames.GrayDark}
        data-elm-id='notify_me_modal_title'
      >
        You'll be notified
      </Text>
    </div>

    <div className={cx('step-subtitle')}>
      <Text
        as='p'
        size='small'
        color={ColorNames.Gray}
        data-elm-id='notify_me_modal_subtitle'
      >
        Emails are good, but texts are faster. Turn on Text Alerts now.
      </Text>
    </div>

    <div className={cx('phone-section')}>
      <Text
        as='p'
        size='small'
        weight='medium'
        color={ColorNames.Gray}
        style={{ marginBottom: 6 }}
      >
        Enable text alerts
      </Text>
      <PhoneVerification.PhoneVerificationReduxForm
        partyId={partyId}
        latestPhone={latestPhone}
        isStrict
        onSuccess={onPhoneVerified}
        onToggle={() => {}}
        phoneInputHelpText=''
      />
    </div>

    {isTwilioBlocked && (
      <div className={cx('opted-out-section')}>
        <Alert
          role='caution'
          content={`You previously opted out of text alerts. To start receiving helpful alerts again, text START${
            smsOptOutNumber ? ` to ${smsOptOutNumber}` : ''
          }.`}
          showIcon
          iconName='mobile-signal'
          data-elm-id='notify_me_modal_opted_out'
        />
      </div>
    )}

    <div className={cx('acknowledgement-section')}>
      <Text
        as='p'
        size='small'
        weight='medium'
        color={ColorNames.Gray}
        style={{ marginBottom: 6 }}
      >
        Acknowledgement
      </Text>
      <CheckboxField
        checked={consentChecked}
        elmId='notify_me_modal_consent'
        label={
          <Text color={ColorNames.Gray} size='mini'>
            {`By checking this box, ${CONSENT_MESSAGE}`}
          </Text>
        }
        onChange={() => onConsentChange(!consentChecked)}
      />
    </div>

    <div className={cx('button-group', 'button-group-no-padding')}>
      <Button
        role='primary'
        isStretch
        elmId='notify_me_modal_confirm_text'
        onClick={onConfirm}
        disabled={isTwilioBlocked || !isPhoneVerified || !consentChecked}
      >
        Confirm Text Alerts
      </Button>
    </div>
  </div>
)

interface AlertMethodCardProps {
  iconName: string
  title: string
  description: string
  enabled: boolean
  showFasterBadge?: boolean
  highlighted?: boolean
  elmId: string
}

const AlertMethodCard = ({
  iconName,
  title,
  description,
  enabled,
  showFasterBadge = false,
  highlighted = false,
  elmId,
}: AlertMethodCardProps) => (
  <div
    className={cx('alert-method-card', {
      'alert-method-card--highlighted': highlighted,
    })}
    data-elm-id={elmId}
  >
    <div className={cx('alert-method-row')}>
      <div className={cx('alert-method-left')}>
        <Icon
          name={iconName}
          iconStyle='regular'
          size='medium'
          color={ColorNames.GrayDark}
        />
        <Text weight='medium' size='small' color={ColorNames.GrayDark}>
          {title}
        </Text>
        {showFasterBadge && <span className={cx('faster-badge')}>FASTER</span>}
      </div>
      <div className={cx('alert-method-status')}>
        <Text
          size='tiny'
          color={enabled ? ColorNames.Success : ColorNames.Caution}
          weight='heavy'
        >
          {enabled ? 'Enabled' : 'Not enabled'}
        </Text>
        {enabled && (
          // 12px matches Figma spec — no DS IconSize maps to this value
          <Icon
            name='check'
            iconStyle='solid'
            color={ColorNames.Success}
            style={{ fontSize: '12px' }}
          />
        )}
      </div>
    </div>
    <Text
      as='p'
      size='mini'
      color={ColorNames.Gray}
      style={{ paddingLeft: 30 }}
    >
      {description}
    </Text>
  </div>
)

export default NotifyMeModal
